import Box from "@mui/material/Box"
import { useTheme } from "@mui/material/styles"
import {
    EEmailProviders,
    EmailAPIFactory,
    EmptyContent,
    FullBar,
    getCalendarFormat,
    getDate,
    getStartOfDate,
    Header,
    LoadingContainer,
    SubNav,
    TColors,
    TEmailReport,
    Typography
} from "cakemail-ui-components-v2"
import { ChartData } from "chart.js"
import { MouseEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Charts } from "../../charts"
import "./styles.scss"
import "../styles.scss"
import { TEngagementTrends } from "./types"
import { ColorIcon } from "../../ColorIcon"
import { TSubNavItem } from "cakemail-ui-components-v2/dist/esm/components/subNav/types"
import { getAPIDateRange, getIntervalBasedOnTimeRange } from "../../../utils/date"
import { calculateMovingAverage, calculateTrendLine, generateTrendLine } from "../../../utils/charts"
import { GraphContainer } from "../../graphContainer"
import { useSearchParams } from "react-router-dom"
import { fillMissingReportIntervals, returnEmailTagsQuery, returnProviderQuery } from "../../../utils/data"
import { emptyReportLog } from "../../../data/emailAPI"
import { Stack } from "@mui/material"
import { LastXDays } from "../../lastXDays"

export function EngagementTrends({ timeRange, tagsFilter, providerFilter, trendRange, availableNavItem, headingTxt, headingTooltipTxt, controlled, controlledProps, filterOperator, refreshKey }: TEngagementTrends) {
    const { t, i18n } = useTranslation()
    const theme = useTheme()
    const [searchParams, setSearchParams] = useSearchParams()
    const showMovingTrend = searchParams.get("mtrend") === "true"
    const showLineTrend = searchParams.get("ltrend") === "true"

    const [stateLoading, setStateLoading] = useState<boolean>(true)
    const [stateMetrics, setStateMetrics] = useState<TEmailReport[]>()
    const [stateTrendMetrics, setStateTrendMetrics] = useState<TEmailReport[]>()
    const [stateError, setStateError] = useState<boolean>(false)

    const [metricType, setMetricType] = useState<keyof TEmailReport>(availableNavItem[0])

    let loading: boolean = false
    let metrics: TEmailReport[] | undefined = undefined
    let trendMetrics: TEmailReport[] | undefined = undefined
    let error: boolean = false

    // To aliviate multiple same component on a page, will minimize the ammount of api call. 
    // You cannot change the controlled property afterwards
    if (controlled) {
        metrics = controlledProps?.metrics
        trendMetrics = controlledProps?.trendMetrics
        loading = controlledProps?.loading || false
        error = controlledProps?.error || false
    } else {
        useEffect(fetchLogs, [timeRange, tagsFilter, refreshKey])
        useEffect(fetchTrendLogs, [timeRange, trendRange, tagsFilter, refreshKey])
        metrics = stateMetrics
        trendMetrics = stateTrendMetrics
        loading = stateLoading
        error = stateError
    }


    function fetchLogs() {
        setStateLoading(true)

        const apiTimeRange = getAPIDateRange({ range: timeRange })
        const interval = getIntervalBasedOnTimeRange(timeRange)

        EmailAPIFactory.getReport({
            interval: interval,
            tags: returnEmailTagsQuery(tagsFilter, { operator: filterOperator }),
            providers: returnProviderQuery({ providers: providerFilter, operator: filterOperator }),
            startTime: apiTimeRange.start,
            endTime: apiTimeRange.end,
        }).then((data) => {
            try {
                fillMissingReportIntervals(data, apiTimeRange.start, apiTimeRange.end, interval, emptyReportLog)
            } catch (e) {
                setStateError(true)
                return
            }

            setStateMetrics(data)
            setStateTrendMetrics(data)
            setStateLoading(false)
            setStateError(false)
        }).catch(() => {
            setStateError(true)
        })
    }

    function fetchTrendLogs() {
        const apiTimeRange = getAPIDateRange({ range: timeRange })
        const interval = getIntervalBasedOnTimeRange(timeRange)
        EmailAPIFactory.getReport({
            interval: interval,
            tags: returnEmailTagsQuery(tagsFilter, { operator: filterOperator }),
            providers: returnProviderQuery({ providers: providerFilter, operator: filterOperator }),
            startTime: apiTimeRange.start,
            endTime: apiTimeRange.end,
        }).then((data) => {
            try {
                fillMissingReportIntervals(data, apiTimeRange.start, apiTimeRange.end, interval, emptyReportLog)
            } catch (e) {
                setStateError(true)
                return
            }
            setStateTrendMetrics(data)
        })
    }

    function handleSubNavChange(event: MouseEvent<HTMLButtonElement>, item: TSubNavItem) {
        setMetricType(item.name as keyof TEmailReport)
    }

    const colors: Partial<{ [K in keyof TEmailReport]: TColors }> = {
        "unique_click_rate": "primary",
        "click_rate": "silver",
        "clickthru_rate": "body1",
        "open_rate": "secondary",
        "unique_open_rate": "info",

        "unsubscribe_rate": "warning",
        "bounce_rate": "silver",
        "spam_rate": "error",
        "reject_rate": "info"
    }

    const navItem: TSubNavItem[] = []

    Object.entries(colors).forEach((entry, index) => {
        const logType = entry[0] as keyof TEmailReport
        const color: TColors = entry[1]

        if (availableNavItem.includes(logType)) {
            navItem.push(
                {
                    name: logType,
                    text: <><ColorIcon color={color} /> {t("general-" + logType)}</>
                }
            )
        }
    })

    const mainData = metrics?.map((report) => {
        return {
            x: report.interval_start * 1000,
            y: report?.[metricType] || 0
        }
    }) || []

    let trendData = trendMetrics?.map((report) => {
        return {
            x: report.interval_start * 1000,
            y: report?.[metricType] || 0
        }
    }) || []

    //For linear trend line
    let regression = trendData.length > 0 ? calculateTrendLine(trendData) : { m: 0, b: 0 }
    let trendLineData = generateTrendLine(trendData, regression)

    const windowSize = trendData ? trendData.length / 3 : 0 // Choose the size of the moving window
    const movingAverageData = trendData.length > 0 ? calculateMovingAverage(trendData, windowSize) : []

    const chartData: ChartData = {
        datasets: [
            {
                type: "line",
                data: mainData,
                yAxisID: 'y',
                borderColor: theme.palette[colors[metricType] as TColors].main,
            }
        ]
    }
    if (showLineTrend) {
        chartData.datasets.push({
            type: "line",
            data: trendLineData,
            yAxisID: 'y1',
            borderColor: theme.palette.body2.main,
            borderDash: [8]
        })
    }


    if (showMovingTrend) {
        chartData.datasets.push({
            type: "line",
            data: movingAverageData,
            yAxisID: 'y1',
            borderColor: theme.palette.wildSand.main,
            borderDash: [8]
        })
    }

    //Override default options
    const options = {
        scales: {
            x: {
                type: 'time',
                min: metrics && metrics.length > 0 ? metrics[0].interval_start * 1000 : getStartOfDate({ add: -timeRange, inMiliseconds: true }),
                max: metrics && metrics.length > 0 ? metrics[metrics.length - 1].interval_start * 1000 : getDate({ format: "unix" }) * 1000,
                /*time: {
                    parser: function (utcMoment) {
                        return utcMoment
                    },
                },*/

                ticks: {
                    maxTicksLimit: 12,
                    callback: function (value: number) {
                        return getCalendarFormat({ unixDate: Number(value) / 1000 })
                    }
                }
            },
            y1: showLineTrend || showMovingTrend ? {} : undefined
        }
    }

    const apiTimeRange = getAPIDateRange({ range: timeRange })
    return (
        <Stack className="analyticBlock engagementTrends">
            <Header
                heading={headingTxt}
                postHeading={<LastXDays apiTimeRange={apiTimeRange} />}
                headingTooltip={headingTooltipTxt}
            />

            <LoadingContainer loading={!metrics && !error} variant="linear">
                {metrics && <GraphContainer loading={loading}>
                    <FullBar
                        left={<SubNav
                            items={navItem}
                            onClick={handleSubNavChange}
                        />}
                    /*
                    //todo re add when trend dorpdown is back and working with graph
                    right={<Typography variant="body2" className="body1Color">{t("email-time-x-trend", { day: timeRange, trend: trendRange })}</Typography>}
                    */
                    />

                    {/*@ts-ignore*/}
                    < Charts type="line" data={chartData} height={"18rem"} options={options} />
                </GraphContainer >}
                {error && <EmptyContent
                    title={t("analytic-block-loading-error-title")}
                />}
            </LoadingContainer >
        </Stack >
    )
}